"use client";

import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { getData } from "country-list";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../ui/sheet";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldSet,
} from "../ui/field";
import { useSaveConfiguration } from "../../hooks/useSaveConfiguration";
import { generatePDFData } from "../../lib/pdf";
import { useConfiguratorContext } from "@studio3dx/legacy-use-configurator/react";
import { SendEnquiry } from "../../app/actions";
import { useMediaQuery } from "../../hooks/use-media-query";
import { cn } from "../../lib/utils";
import { resolveSectionCategoryLabel } from "../../lib/resolveSectionCategoryLabel";
import harbor from "@studio3dx/harbor-js";

/** Palette aligned with caroffshore.it — warm paper, charcoal ink, Mediterranean teal accent */
const co = {
  shell: "border-stone-200/90 bg-[#f6f4f0]",
  header: "border-b border-stone-200/80 bg-white/50 backdrop-blur-sm",
  kicker: "text-[10px] font-medium uppercase tracking-[0.28em] text-[#5a726f]",
  title:
    "font-serif text-2xl font-normal tracking-tight text-stone-900 sm:text-[1.65rem]",
  subtitle: "text-sm text-stone-600",
  label: "text-[11px] font-medium uppercase tracking-[0.16em] text-stone-500",
  field:
    "rounded-sm border-stone-200/90 bg-white shadow-none placeholder:text-stone-400 focus-visible:border-[#5a7f7f] focus-visible:ring-[#5a7f7f]/25",
  primaryBtn:
    "h-11 w-full rounded-sm bg-[#1c1917] text-[13px] font-medium uppercase tracking-[0.14em] text-[#fafaf9] hover:bg-stone-800 sm:w-auto sm:min-w-[200px]",
  outlineBtn: "rounded-sm border-stone-300 text-stone-700 hover:bg-stone-100",
  successTitle: "font-serif text-2xl font-normal text-stone-900",
  successBody: "text-sm leading-relaxed text-stone-600",
} as const;

const COUNTRIES = [...getData()].sort((a, b) =>
  a.name.localeCompare(b.name, "en"),
);
const COUNTRY_NAME_SET = new Set(COUNTRIES.map((c) => c.name));

const formSchema = z.object({
  modelName: z.string(),
  garageCode: z.string(),
  garageCodeUrl: z.string(),
  pdfUrl: z.string(),
  message: z
    .string()
    .min(10, { message: "Message must be at least 10 characters long" }),
  firstName: z
    .string()
    .min(2, { message: "First name must be at least 2 characters long" }),
  lastName: z
    .string()
    .min(2, { message: "Last name must be at least 2 characters long" }),
  country: z
    .string()
    .min(1, { message: "Please select a country" })
    .refine((v) => COUNTRY_NAME_SET.has(v), {
      message: "Please select a country",
    }),
  city: z
    .string()
    .min(2, { message: "City must be at least 2 characters long" }),
  email: z.string().email({ message: "Please enter a valid email address" }),
  phone: z.string().optional(),
  favorite_color: z.string().optional(),
});

function EnquiryShellHeader({
  modelName,
  className,
}: {
  modelName: string;
  className?: string;
}) {
  return (
    <div className={cn(co.header, "px-6 py-5", className)}>
      <p className={co.kicker}>Car Off Shore</p>
      <h2 className={cn(co.title, "mt-2")}>Enquiry</h2>
      <p className={cn(co.subtitle, "mt-1.5")}>
        {modelName}
        <span className="mx-2 text-stone-300">·</span>
        <span className="italic text-stone-500">Fatto in Italia</span>
      </p>
    </div>
  );
}

function EnquiryForm({
  className,
  form,
  submitted,
  submitting,
  onSubmit,
}: {
  className?: string;
  form: ReturnType<typeof useForm<z.infer<typeof formSchema>>>;
  submitted: boolean;
  submitting: boolean;
  onSubmit: (data: z.infer<typeof formSchema>) => void;
}) {
  const {
    formState: { errors },
  } = form;

  if (submitted) {
    return (
      <div className={cn("space-y-4 px-6 py-8 text-center sm:px-8", className)}>
        <p className={co.successTitle}>Thank you</p>
        <p className={co.successBody}>
          Your enquiry has been sent. You will shortly receive a confirmation
          email with more information. A member of our team will be in touch.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className={className}>
      <FieldSet className="space-y-0">
        <FieldGroup className="gap-6 @container/field-group">
          <Field data-invalid={!!errors.message}>
            <FieldLabel htmlFor="message" className={co.label}>
              Your message
            </FieldLabel>
            <Controller
              name="message"
              control={form.control}
              render={({ field }) => (
                <Textarea
                  id="message"
                  className={cn(
                    "min-h-[100px] resize-none sm:min-h-[120px]",
                    co.field,
                  )}
                  aria-invalid={!!errors.message}
                  {...field}
                />
              )}
            />
            <FieldError errors={errors.message ? [errors.message] : []} />
          </Field>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-4">
            <Field data-invalid={!!errors.firstName}>
              <FieldLabel htmlFor="firstName" className={co.label}>
                First name
              </FieldLabel>
              <Controller
                name="firstName"
                control={form.control}
                render={({ field }) => (
                  <Input
                    id="firstName"
                    className={co.field}
                    aria-invalid={!!errors.firstName}
                    {...field}
                  />
                )}
              />
              <FieldError errors={errors.firstName ? [errors.firstName] : []} />
            </Field>

            <Field data-invalid={!!errors.lastName}>
              <FieldLabel htmlFor="lastName" className={co.label}>
                Last name
              </FieldLabel>
              <Controller
                name="lastName"
                control={form.control}
                render={({ field }) => (
                  <Input
                    id="lastName"
                    className={co.field}
                    aria-invalid={!!errors.lastName}
                    {...field}
                  />
                )}
              />
              <FieldError errors={errors.lastName ? [errors.lastName] : []} />
            </Field>
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-4">
            <Field data-invalid={!!errors.city}>
              <FieldLabel htmlFor="city" className={co.label}>
                City
              </FieldLabel>
              <Controller
                name="city"
                control={form.control}
                render={({ field }) => (
                  <Input
                    id="city"
                    className={co.field}
                    aria-invalid={!!errors.city}
                    {...field}
                  />
                )}
              />
              <FieldError errors={errors.city ? [errors.city] : []} />
            </Field>

            <Field data-invalid={!!errors.country}>
              <FieldLabel htmlFor="country" className={co.label}>
                Country
              </FieldLabel>
              <Controller
                name="country"
                control={form.control}
                render={({ field }) => (
                  <select
                    id="country"
                    className={cn(
                      "h-10 w-full cursor-pointer rounded-sm border px-3 py-2 text-sm text-stone-900 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50",
                      co.field,
                    )}
                    aria-invalid={!!errors.country}
                    {...field}
                  >
                    <option value="" disabled>
                      Select country
                    </option>
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                )}
              />
              <FieldError errors={errors.country ? [errors.country] : []} />
            </Field>
          </div>

          <Field data-invalid={!!errors.email}>
            <FieldLabel htmlFor="email" className={co.label}>
              Email
            </FieldLabel>
            <Controller
              name="email"
              control={form.control}
              render={({ field }) => (
                <Input
                  id="email"
                  type="email"
                  className={co.field}
                  aria-invalid={!!errors.email}
                  {...field}
                />
              )}
            />
            <FieldError errors={errors.email ? [errors.email] : []} />
          </Field>

          <Field data-invalid={!!errors.phone}>
            <FieldLabel htmlFor="phone" className={co.label}>
              Phone{" "}
              <span className="font-normal normal-case tracking-normal text-stone-400">
                (optional)
              </span>
            </FieldLabel>
            <Controller
              name="phone"
              control={form.control}
              render={({ field }) => (
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+39 …"
                  className={co.field}
                  aria-invalid={!!errors.phone}
                  {...field}
                />
              )}
            />
            <FieldDescription className="text-xs text-stone-500">
              Include your country code for international numbers.
            </FieldDescription>
            <FieldError errors={errors.phone ? [errors.phone] : []} />
          </Field>

          <div className="hidden">
            <Controller
              name="favorite_color"
              control={form.control}
              render={({ field }) => (
                <Input id="favorite_color" tabIndex={-1} {...field} />
              )}
            />
          </div>

          <Field orientation="horizontal" className="pt-2">
            <Button
              type="submit"
              className={co.primaryBtn}
              disabled={submitting}
            >
              {submitting ? "Sending…" : "Send enquiry"}
            </Button>
          </Field>
        </FieldGroup>
      </FieldSet>
    </form>
  );
}

export default function EnquiryDialog({
  label = "Request quotation & enquiry",
}: {
  label?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 768px)");

  const configurator = useConfiguratorContext();
  const { configuratorName } = configurator;
  const { save, code } = useSaveConfiguration(
    configuratorName,
    configurator.getAllSelectedOptionIds(),
  );

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      modelName: configuratorName,
      message: "",
      firstName: "",
      lastName: "",
      city: "",
      country: "",
      email: "",
      phone: "",
      favorite_color: "",
    },
  });

  useEffect(() => {
    if (isOpen)
      save().then((code) => {
        form.setValue("garageCode", code);
        form.setValue(
          "garageCodeUrl",
          `${process.env.NEXT_PUBLIC_APP_URL}/garage-code/${code}`,
        );
        form.setValue(
          "pdfUrl",
          `${process.env.NEXT_PUBLIC_APP_URL}/garage-code/${code}/pdf`,
        );
      });
  }, [isOpen, configuratorName]);

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      formData.append(key, value);
    });

    if (formData.get("favorite_color") !== "") {
      return;
    }

    const additionalData: any = {
      model: configuratorName,
      garageCode: code,
      options: [],
    };

    for (const section of configurator.getAllSections()) {
      for (const option of configurator.getSelectedOptions(section.id)) {
        additionalData.options.push({
          category: resolveSectionCategoryLabel(section),
          optionCode: option?.id,
          description: option.label,
        });
      }
    }
    try {
      setSubmitting(true);
      await SendEnquiry(formData, additionalData);

      harbor.identify(data.email, {
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone,
        city: data.city,
        country: data.country,
      });

      form.reset();
      setSubmitted(true);
    } catch (error) {
      console.error(error);
      alert("Failed to send enquiry.");
    }

    setSubmitting(false);
  };

  const triggerClass = cn(
    "w-full rounded-sm border border-stone-800/10 bg-[#1c1917] text-[12px] font-medium uppercase tracking-[0.12em] text-[#fafaf9] shadow-sm",
    "hover:bg-stone-800",
  );

  if (isDesktop) {
    return (
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Button type="button" className={triggerClass}>
            Request quotation & enquiry
          </Button>
        </DialogTrigger>
        <DialogContent
          className={cn(
            co.shell,
            "max-h-[90vh] max-w-xl gap-0 overflow-hidden p-0 sm:rounded-sm",
          )}
        >
          {submitted ? (
            <div className="flex flex-col">
              <EnquiryShellHeader modelName={configuratorName} />
              <DialogHeader className="sr-only">
                <DialogTitle>Thank you</DialogTitle>
                <DialogDescription>
                  Your enquiry has been sent.
                </DialogDescription>
              </DialogHeader>
              <EnquiryForm
                form={form}
                submitted={submitted}
                submitting={submitting}
                onSubmit={onSubmit}
              />
            </div>
          ) : (
            <>
              <EnquiryShellHeader modelName={configuratorName} />
              <DialogHeader className="sr-only">
                <DialogTitle>Enquiry</DialogTitle>
              </DialogHeader>
              <div className="max-h-[calc(90vh-7rem)] overflow-y-auto px-6 py-6 sm:px-8">
                <EnquiryForm
                  form={form}
                  submitted={submitted}
                  submitting={submitting}
                  onSubmit={onSubmit}
                />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button type="button" className={triggerClass}>
          {label}
        </Button>
      </SheetTrigger>
      <SheetContent
        side="bottom"
        className={cn(
          co.shell,
          "max-h-[92vh] gap-0 overflow-hidden rounded-t-lg p-0",
        )}
      >
        {submitted ? (
          <>
            <SheetHeader className="p-0 text-left">
              <EnquiryShellHeader modelName={configuratorName} />
              <SheetTitle className="sr-only">Thank you</SheetTitle>
            </SheetHeader>
            <EnquiryForm
              form={form}
              submitted={submitted}
              submitting={submitting}
              onSubmit={onSubmit}
            />
          </>
        ) : (
          <>
            <SheetHeader className={cn("p-0 text-left")}>
              <EnquiryShellHeader
                modelName={configuratorName}
                className="rounded-none border-0"
              />
              <SheetTitle className="sr-only">Enquiry</SheetTitle>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto px-6 pb-4 pt-2">
              <EnquiryForm
                form={form}
                submitted={submitted}
                submitting={submitting}
                onSubmit={onSubmit}
              />
            </div>
            <SheetFooter className="flex-shrink-0 border-t border-stone-200/80 bg-white/40 px-6 py-4">
              <SheetClose asChild>
                <Button
                  type="button"
                  variant="outline"
                  className={co.outlineBtn}
                >
                  Cancel
                </Button>
              </SheetClose>
            </SheetFooter>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
